import React, { useCallback, useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'

import CustomSideMenu from '../SidebarMenu/CustomSideMenu'
import NavigationBar from '../NavigationBar/NavigationBar'
import SettingsHeader from './Header/SettingsHeader'
import BackButton from './Header/BackButton'
import {
    resetFloatPopup,
    setNavigationRoute,
    setSelectedNavItem,
    setSelectedSidebarTab,
    setShowAccessDeniedPopup,
    storeCurrentUser,
} from '../../redux/actions'
import store from '../../redux/store'
import Customizations from './Customizations/Customizations'
import ProjectsSettings from './ProjectsSettings/ProjectsSettings'
import ProjectsInvitations from './Invitations/ProjectsInvitations'
import ProjectInvitationPopup from '../UIComponents/ProjectInvitation/ProjectInvitationPopup'
import UserStatistics from './Statistics/UserStatistics'
import UserHappiness from './Happiness/UserHappiness'
import UserOKRs from './OKRs/UserOKRs'
import CustomScrollView from '../UIControls/CustomScrollView'
import ShortcutsSection from './Shortcuts/ShortcutsSection'
import {
    DV_TAB_ROOT_TASKS,
    DV_TAB_SETTINGS_INVITATIONS,
    DV_TAB_SETTINGS_PREMIUM,
    DV_TAB_SETTINGS_PROJECTS,
    DV_TAB_SETTINGS_SHORTCUTS,
    DV_TAB_SETTINGS_STATISTICS,
    DV_TAB_SETTINGS_CUSTOMIZATIONS,
    DV_TAB_SETTINGS_PROFILE,
    DV_TAB_SETTINGS_EXPORT,
    DV_TAB_SETTINGS_HAPPINESS,
    DV_TAB_SETTINGS_OKRS,
    DV_TAB_SETTINGS_MCP,
    DV_TAB_SETTINGS_INTEGRATIONS,
    DV_TAB_SETTINGS_SKILLS,
} from '../../utils/TabNavigationConstants'
import StripePremiumTab from '../Premium/PremiumTab/StripePremiumTab'
import IapPremiumTab from '../Premium/PremiumTab/IapPremiumTab'
import { isCapacitorIosShell } from '../../utils/CapacitorShell'
import ExportTab from './Export/ExportTab'
import MCPSettings from './MCP/MCPSettings'
import IntegrationsSettings from './Integrations/IntegrationsSettings'
import SettingsSkills from './Skills/SettingsSkills'
import { useDispatch, useSelector } from 'react-redux'
import LoadingData from '../UIComponents/LoadingData'
import UserProfileSettings from './Profile/UserProfileSettings'
import DragModalsContainer from '../UIComponents/FloatModals/DragModalsContainer'
import { SIDEBAR_MENU_COLLAPSED_WIDTH } from '../styles/global'
import useCollapsibleSidebar from '../SidebarMenu/Collapsible/UseCollapsibleSidebar'
import { countBrokenConnections, countBrokenVmSubscriptions } from './Integrations/connectionHealth'
import { getVmSubscriptionStatus } from '../../utils/backends/firestore'

const SettingsView = ({ navigation }) => {
    const dispatch = useDispatch()
    const scrollRef = useRef(null)
    const selectedTab = useSelector(state => state.selectedNavItem)
    const settingsScrollToTopToken = useSelector(state => state.settingsScrollToTopToken)
    const mobile = useSelector(state => state.smallScreenNavigation)
    const isMiddleScreen = useSelector(state => state.isMiddleScreen)
    const showProjectInvitationPopup = useSelector(state => state.showProjectInvitationPopup.visible)
    const userIsAnonymous = useSelector(state => state.loggedUser.isAnonymous)
    const amountProjectInvitations = useSelector(state => state.loggedUser.invitedProjectIds.length)
    // Broken email/calendar connections badge the Integrations tab. Selected as a count,
    // not as the connection objects, so an unrelated write to the user document cannot
    // re-render the whole settings shell (AT-2336's rule).
    const brokenConnectionsAmount = useSelector(state => countBrokenConnections(state.loggedUser))
    const [brokenVmSubscriptionsAmount, setBrokenVmSubscriptionsAmount] = useState(0)
    const updateVmSubscriptionAlerts = useCallback(
        status => setBrokenVmSubscriptionsAmount(countBrokenVmSubscriptions(status)),
        []
    )

    // VM subscription state is private and therefore is not part of loggedUser's live snapshot.
    // Load its small, sanitized status when Settings opens so the Integrations tab can warn before
    // the user opens it; the section reports later reconnects back through the same callback.
    useEffect(() => {
        let cancelled = false
        getVmSubscriptionStatus()
            .then(status => {
                if (!cancelled) updateVmSubscriptionAlerts(status)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [updateVmSubscriptionAlerts])

    const { overlay } = useCollapsibleSidebar()

    const navigationTabs = [
        DV_TAB_SETTINGS_PROFILE,
        DV_TAB_SETTINGS_CUSTOMIZATIONS,
        DV_TAB_SETTINGS_PROJECTS,
        DV_TAB_SETTINGS_INVITATIONS,
        DV_TAB_SETTINGS_STATISTICS,
        DV_TAB_SETTINGS_HAPPINESS,
        DV_TAB_SETTINGS_OKRS,
        DV_TAB_SETTINGS_SKILLS,
        DV_TAB_SETTINGS_SHORTCUTS,
        DV_TAB_SETTINGS_EXPORT,
        DV_TAB_SETTINGS_MCP,
        DV_TAB_SETTINGS_INTEGRATIONS,
        DV_TAB_SETTINGS_PREMIUM,
    ]

    useEffect(() => {
        if (userIsAnonymous) {
            dispatch([resetFloatPopup(), setSelectedSidebarTab(DV_TAB_ROOT_TASKS), setShowAccessDeniedPopup(true)])
            navigation.navigate('Root')
        } else {
            const tab = navigationTabs.includes(selectedTab) ? selectedTab : DV_TAB_SETTINGS_PROFILE
            dispatch([setSelectedNavItem(tab)])
        }
    }, [])

    useEffect(() => {
        const { currentUser, loggedUser } = store.getState()
        if (!!currentUser.recorderUserId || !!currentUser.temperature) {
            dispatch(storeCurrentUser(loggedUser))
        }
    }, [])

    useEffect(() => {
        if (settingsScrollToTopToken && selectedTab === DV_TAB_SETTINGS_PROFILE) {
            scrollRef.current?.scrollTo({ y: 0, animated: false })
        }
    }, [settingsScrollToTopToken, selectedTab])

    return (
        <View style={localStyles.container}>
            {showProjectInvitationPopup && <ProjectInvitationPopup navigation={navigation} />}
            {!mobile ? <CustomSideMenu navigation={navigation} isWeb /> : null}

            <View style={{ flex: 1 }}>
                {!isMiddleScreen && (
                    <View
                        style={[
                            localStyles.backButtonContainer,
                            overlay && { marginLeft: SIDEBAR_MENU_COLLAPSED_WIDTH },
                        ]}
                    >
                        <BackButton />
                    </View>
                )}

                <CustomScrollView
                    ref={scrollRef}
                    style={[
                        localStyles.scrollPanel,
                        mobile ? localStyles.scrollPanelMobile : isMiddleScreen && localStyles.scrollPanelTablet,
                        overlay && { marginLeft: SIDEBAR_MENU_COLLAPSED_WIDTH },
                    ]}
                >
                    <View style={localStyles.scrollContainer}>
                        <SettingsHeader />
                        <View style={mobile ? localStyles.navigationBar : undefined}>
                            <NavigationBar
                                taskDetail
                                isSecondary
                                tabs={navigationTabs}
                                invitationsAmount={amountProjectInvitations}
                                integrationsAlertAmount={brokenConnectionsAmount + brokenVmSubscriptionsAmount}
                            />
                        </View>
                        {(() => {
                            switch (selectedTab) {
                                case DV_TAB_SETTINGS_PROFILE:
                                    return <UserProfileSettings />
                                case DV_TAB_SETTINGS_CUSTOMIZATIONS:
                                    return <Customizations />
                                case DV_TAB_SETTINGS_PROJECTS:
                                    return <ProjectsSettings />
                                case DV_TAB_SETTINGS_INVITATIONS:
                                    return <ProjectsInvitations />
                                case DV_TAB_SETTINGS_STATISTICS:
                                    return <UserStatistics />
                                case DV_TAB_SETTINGS_HAPPINESS:
                                    return <UserHappiness />
                                case DV_TAB_SETTINGS_OKRS:
                                    return <UserOKRs />
                                case DV_TAB_SETTINGS_SKILLS:
                                    return <SettingsSkills />
                                case DV_TAB_SETTINGS_SHORTCUTS:
                                    return <ShortcutsSection />
                                case DV_TAB_SETTINGS_PREMIUM:
                                    // iOS shell: digital goods must go through Apple IAP
                                    // (guideline 3.1.1) — same tab, StoreKit instead of Stripe.
                                    return isCapacitorIosShell() ? <IapPremiumTab /> : <StripePremiumTab />
                                case DV_TAB_SETTINGS_EXPORT:
                                    return <ExportTab />
                                case DV_TAB_SETTINGS_MCP:
                                    return <MCPSettings />
                                case DV_TAB_SETTINGS_INTEGRATIONS:
                                    return (
                                        <IntegrationsSettings
                                            onVmSubscriptionStatusChanged={updateVmSubscriptionAlerts}
                                        />
                                    )
                            }
                        })()}
                    </View>
                </CustomScrollView>
                <DragModalsContainer />
            </View>
            <LoadingData />
        </View>
    )
}

export default SettingsView

const localStyles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: 'white',
    },
    backButtonContainer: {
        position: 'absolute',
        top: 0,
        left: 32,
        zIndex: 100,
    },
    scrollPanel: {
        flex: 1,
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingHorizontal: 104,
    },
    scrollPanelMobile: {
        paddingHorizontal: 16,
    },
    scrollPanelTablet: {
        paddingHorizontal: 56,
    },
    scrollContainer: {
        flexDirection: 'column',
        backgroundColor: 'white',
        flex: 1,
    },
    navigationBar: {
        marginHorizontal: -16,
    },
})
